import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";
import EditableCell from "./EditableCell";
import Restore from "../../modules/applicants/Icons/Restore.svg";
import Garbage from "../../modules/applicants/Icons/Garbage.svg";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import LongTextPopUp from "../LongTextPopUp";
import { useSelector } from "react-redux";
import { isLeaderSelector } from "../../store/applicantsSlice";

const useStyles = makeStyles((theme) => ({
  actions: {
    display: "flex",
  },
  tableCell: {},
  saveButton: {
    maxHeight: 35,
    margin: "auto 0 auto 0",
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  editButton: {
    maxHeight: 35,
    margin: "auto 0 auto 0",
    "&:hover": {
      backgroundColor: theme.palette.gif.yellow.hover,
    },
    color: theme.palette.gif.yellow.dark,
    backgroundColor: theme.palette.gif.yellow.color,
  },
}));

const DeleteDialog = (props) => {
  const { open, onClose } = props;

  return (
    <Dialog open={open}>
      <DialogTitle>{"Confirm Delete"}</DialogTitle>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="primary">
          No
        </Button>
        <Button onClick={() => onClose(true)} color="primary" autoFocus>
          Yes, Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function EditableRow(props) {
  const { row, tableConfig, setRow, deleteRow, validateRow, create, ...rest } =
    props;
  const classes = useStyles();
  const isLeader = useSelector(isLeaderSelector);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  let currentRow = { ...row };

  const handleChange = (e) => {
    const { name, value } = e.target;
    currentRow[name] = value;
  };
  const RegularCell = ({ config, index }) => {
    let value = row[config.field];
    if (config.options) {
      value = config.options.find((o) => o.id === value)?.label;
    }
    if (config.type === "calculated") {
      value = config.calculation(row);
    }
    if (config.type === "date") {
      var date = new Date(value);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      value = `${day}-${month}-${year}`;
    }
    return (
      <TableCell
        className={classes.tableCell}
        align={config.align}
        component={index === 0 ? "th" : ""}
        scope={index === 0 ? "row" : ""}
      >
        {config.type === "longText" ? (
          <>
            <LongTextPopUp value={value} open={open} setOpen={setOpen} />
            <IconButton
              style={{ marginLeft: 20 }}
              onClick={() => setOpen(!open)}
              size="large"
            >
              <CommentOutlinedIcon fontSize="large" />
            </IconButton>
          </>
        ) : (
          value
        )}
      </TableCell>
    );
  };

  const onDialogClose = (result) => {
    setConfirmOpen(false);
    if (result) {
      deleteRow(currentRow);
    }
  };

  const handleSave = () => {
    if (!validateRow(currentRow)) return;
    setEditing(false);
    setRow(currentRow);
  };
  const handleDelete = async () => {
    setConfirmOpen(true);
  };
  const handleCreate = () => {
    if (!validateRow(currentRow)) return;
    setRow(currentRow);
  };

  const disableEdit = () => {
    if (tableConfig.disableEdit) return true;
    if (
      tableConfig.editOnlyId &&
      !isLeader &&
      row[tableConfig.editOnlyIdFiled] !== tableConfig.editOnlyId
    )
      return true;
    return false;
  };

  const RegularRow = () => (
    <TableRow {...rest}>
      {tableConfig.columns
        .filter((tc) => !tc.hidden)
        .map((config, index) => (
          <RegularCell
            config={config}
            index={index}
            key={index}
            style={{ maxWidth: config.width }}
          />
        ))}
      <TableCell>
        {disableEdit() ? (
          <></>
        ) : (
          <Box className={classes.actions}>
            <Button
              disableElevation
              variant="contained"
              size="small"
              className={classes.editButton}
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            <IconButton onClick={handleDelete} size="large">
              <img src={Garbage} width="23" alt="React Logo" />
            </IconButton>
            <DeleteDialog open={confirmOpen} onClose={onDialogClose} />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );

  const EditRow = () => (
    <TableRow {...rest}>
      {tableConfig.columns
        .filter((tc) => !tc.hidden)
        .map((config, index) => (
          <EditableCell
            key={index}
            autoFocus={index === 0}
            config={config}
            index={index}
            name={config.id}
            value={row[config.field]}
            onChange={handleChange}
          />
        ))}
      <TableCell>
        <Box className={classes.actions}>
          <Button
            disableElevation
            variant="contained"
            size="small"
            className={classes.saveButton}
            onClick={handleSave}
          >
            Save
          </Button>
          <Tooltip title="Revert">
            <IconButton onClick={() => setEditing(false)} size="large">
              <img src={Restore} width="23" alt="React Logo" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
  const CreateRow = () => (
    <TableRow {...rest}>
      {tableConfig.columns
        .filter((tc) => !tc.hidden)
        .map((config, index) => (
          <EditableCell
            key={index}
            autoFocus={index === 0}
            config={config}
            index={index}
            name={config.id}
            value={config.value}
            onChange={handleChange}
          />
        ))}
      <TableCell>
        <Box className={classes.actions}>
          <Button
            disableElevation
            variant="contained"
            size="small"
            className={classes.saveButton}
            onClick={handleCreate}
          >
            Save
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
  if (create) return <CreateRow />;
  if (editing) return <EditRow />;
  return <RegularRow />;
}
